import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { companyService } from '../services/companyService';

const SESSION_STORAGE_KEY = 'superAdminActiveCompanyId';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allCompanies, setAllCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyIdState] = useState(null);

  useEffect(() => {
    // Get initial session
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session?.user?.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session?.user?.id);
      } else {
        setUserProfile(null);
        setAllCompanies([]);
        setActiveCompanyIdState(null);
        setLoading(false);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const fetchUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*, companies(*)')?.eq('id', userId)?.single();

      if (error) throw error;
      setUserProfile(data);

      if (data?.role === 'super_admin') {
        try {
          const companies = await companyService.getAllCompanies();
          setAllCompanies(companies);

          const storedId = sessionStorage.getItem(SESSION_STORAGE_KEY);
          const storedExists = storedId && companies.some(c => c.id === storedId);
          const initialId = storedExists ? storedId : (companies[0]?.id ?? data?.company_id ?? null);
          setActiveCompanyIdState(initialId);
        } catch (err) {
          console.error('Error fetching companies:', err?.message);
          setAllCompanies([]);
          setActiveCompanyIdState(data?.company_id ?? null);
        }
      } else {
        setActiveCompanyIdState(data?.company_id ?? null);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const setActiveCompany = (companyId) => {
    if (userProfile?.role !== 'super_admin') return;
    sessionStorage.setItem(SESSION_STORAGE_KEY, companyId);
    setActiveCompanyIdState(companyId);
  };

  const signUp = async (email, password, metadata) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (error) {
        if (error?.message?.includes('Email not confirmed')) {
          throw new Error('Please verify your email address before signing in. Check your inbox for a confirmation link.');
        }
        if (error?.message?.includes('Invalid login credentials')) {
          throw new Error('Invalid email or password. Please check your credentials and try again.');
        }
        throw error;
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      setUser(null);
      setUserProfile(null);
      setAllCompanies([]);
      setActiveCompanyIdState(null);
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (error) {
      console.error('Error signing out:', error?.message);
      throw error;
    }
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at != null;
  };

  const resetPasswordForEmail = async (email) => {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      const { error } = await supabase?.auth?.updateUser({ password: newPassword });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const isSuperAdmin = userProfile?.role === 'super_admin';

  const activeCompany = allCompanies.find(c => c.id === activeCompanyId)
    ?? (userProfile?.companies ? {
        id: userProfile.companies.id,
        companyName: userProfile.companies.company_name,
        logoUrl: userProfile.companies.logo_url ?? null
      } : null);

  const value = {
    user,
    userProfile,
    loading,
    signUp,
    signIn,
    signOut,
    isEmailVerified,
    resetPasswordForEmail,
    updatePassword,
    isSuperAdmin,
    activeCompanyId,
    activeCompany,
    allCompanies,
    setActiveCompany
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
